/*
 *   Copyright (c) 2023 Propally.Inc
 *   All rights reserved.
 */

/**
 * PushNotifications 関連のエラーオブジェクトからメッセージ文字列を取り出す
 * string / Error / `{ message }` / `{ error }` の順に判定する（既存実装のロジックを共通化）
 */
const extractPushErrorMessage = (error: unknown): string | undefined => {
  if (!error) return undefined;
  return typeof error === "string"
    ? error
    : error instanceof Error
    ? error.message
    : typeof (error as Record<string, unknown>)?.message === "string"
    ? ((error as Record<string, unknown>).message as string)
    : typeof (error as Record<string, unknown>)?.error === "string"
    ? ((error as Record<string, unknown>).error as string)
    : undefined;
};

/**
 * プッシュ通知の許可が下りていないことによるエラーかどうかを判定する
 * ユーザ操作起因の想定内の失敗のため独立した Sentry イベントにはせず console.info に落とす
 * この判定も下の isTransientPushRegistrationError と同じくロケール依存（英語文言前提）で、
 * 日本語端末などでは一致せず従来どおり console.error に落ちる
 */
export const isNotificationsDisallowedError = (error: unknown): boolean => {
  const message = extractPushErrorMessage(error);
  if (!message) return false;
  return message.toLowerCase().includes("notifications are not allowed");
};

export const DEVICE_REGISTRATION_LIMIT_ERROR_CATEGORY = "registration_limit";

/**
 * FCM登録数上限（TOO_MANY_REGISTRATIONS）によるエラーかどうかを判定する
 * 一時的なネットワーク失敗とは区別し、回復方法や恒久性はこの分類から断定しない
 * AUTHENTICATION_FAILED のようにアプリ側で直せる設定不備とは区別し、そちらは従来どおり error のまま送る（PROPALLY_WEB-25Y）
 * Android は FCM 例外の定数文字列が来るためロケール依存はない
 */
export const isDeviceRegistrationLimitError = (error: unknown): boolean => {
  const message = extractPushErrorMessage(error);
  if (!message) return false;
  return message.toLowerCase().includes("too_many_registrations");
};

/**
 * APNs / FCM の登録失敗が一時的な不調によるものかどうかを判定する
 * 一時的な失敗は console.error にせず、pushRegistrationMonitor で送信頻度を制限して観測する
 * パターンはこの範囲に限定し、本物の登録失敗を握りつぶさないよう安易に広げないこと
 *
 * なお iOS は error.localizedDescription を返すため文言は端末のロケールに依存し、
 * 英語・日本語以外のロケールではこのパターンに一致しない
 * Android は FCM 例外の getLocalizedMessage()（ロケール非依存の定数文字列）が来る
 * いずれも一致しなかった場合は従来どおり console.error に落ちて Sentry に送られるため、安全側に倒れる
 *
 * Android の FCM 定数のうち AUTHENTICATION_FAILED は設定不備による恒久的な失敗で、
 * 実際に本番で発生している（Sentry PROPALLY_WEB-25Y）ため意図的に対象外にしている
 * TIMEOUT / INTERNAL_SERVER_ERROR も一時的ではあるが発生実績がないため入れていない
 *
 * 日本語ロケールの iOS で Sentry に実際に上がった次の実ペイロードに基づき追加している（推測での拡張ではない）
 * - PROPALLY_WEB-283: 「リクエストがタイムアウトになりました。」→ timeout（従来の「要求がタイムアウトしました」はバージョン差を考慮して残す）
 * - PROPALLY_WEB-282: 「TLSエラーが起きたため、セキュリティ保護された接続を確立できませんでした。」→ tls
 */
export const TRANSIENT_PUSH_REGISTRATION_ERROR_CATEGORIES = [
  "network_lost",
  "offline",
  "timeout",
  "tls",
  "service_unavailable",
] as const;

export type TransientPushRegistrationErrorCategory = (typeof TRANSIENT_PUSH_REGISTRATION_ERROR_CATEGORIES)[number];

export const getTransientPushRegistrationErrorCategory = (
  error: unknown
): TransientPushRegistrationErrorCategory | undefined => {
  const message = extractPushErrorMessage(error);
  if (!message) return undefined;
  const lowerMessage = message.toLowerCase();
  const patterns: Array<[TransientPushRegistrationErrorCategory, string[]]> = [
    ["network_lost", ["network connection was lost", "ネットワーク接続が切れました"]],
    ["offline", ["internet connection appears to be offline", "インターネット接続がオフラインのようです"]],
    ["timeout", ["request timed out", "要求がタイムアウトしました", "リクエストがタイムアウトになりました"]],
    ["tls", ["an ssl error has occurred", "tlsエラーが起きたため"]],
    // FCM ではリトライ可でも、端末環境によって恒常失敗になるため継続回数を観測する。
    ["service_unavailable", ["service_not_available"]],
  ];
  return patterns.find(([, messages]) => messages.some((pattern) => lowerMessage.includes(pattern)))?.[0];
};

export const isTransientPushRegistrationError = (error: unknown): boolean =>
  getTransientPushRegistrationErrorCategory(error) !== undefined;
